import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { get, isNil, meanBy, sumBy, startCase } from 'es-toolkit/compat'
import { unitConvert } from '@farmtrx/units'
import { getDistance } from 'ol/sphere'
import { toLonLat } from 'ol/proj'
import { getCenter, boundingExtent } from 'ol/extent'
import { V } from 'components/layout'
import { styled } from '@mui/system'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Busy from 'components/busy'
import * as c from 'common/c'

const StyledContainer = styled('div')(({ theme }) => ({
  minWidth: '200px',
  padding: theme.spacing(1),
}))

const StatRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '4px',
})

const Label = styled(Typography)({
  fontWeight: 'bold',
  marginRight: '8px',
})

const Value = styled(Typography)({})

function getCentroid(geometry) {
  if (geometry.type === 'Polygon') {
    // geometry.coordinates is an array of rings, each ring is an array of points.
    // boundingExtent expects an array of points [x,y].
    // We take the outer ring.
    if (geometry.coordinates && geometry.coordinates.length > 0) {
      const ring = geometry.coordinates[0]
      if (ring.length > 0) {
        const extent = boundingExtent(ring)
        return getCenter(extent)
      }
    }
  } else if (geometry.type === 'MultiPolygon') {
    // Just take first polygon for simplicity
    // Assume simple polygons for swath
    if (
      geometry.coordinates &&
      geometry.coordinates.length > 0 &&
      geometry.coordinates[0].length > 0
    ) {
      const ring = geometry.coordinates[0][0]
      const extent = boundingExtent(ring)
      return getCenter(extent)
    }
  } else if (geometry.type === 'Point') {
    return geometry.coordinates
  }
  return [0, 0]
}

export default function SwathInfoView() {
  const { t } = useTranslation()
  const data = useSelector((state) => get(state, 'map.swathInfoRes'))
  const prefs = useSelector((state) => state.account.singleton.prefs)
  const harvest = useSelector((state) => get(state, 'harvest.current'))
  const { units } = prefs

  const stats = useMemo(() => {
    if (
      isNil(data) ||
      data === c.UNDEFINED ||
      isNil(data.features) ||
      data.features.length === 0
    ) {
      return null
    }

    const features = data.features

    // Sort by timestamp if available for distance calc
    const sortedFeatures = [...features].sort((a, b) => {
      const timeA = a.properties.timestamp || a.properties.Time || 0
      const timeB = b.properties.timestamp || b.properties.Time || 0
      return timeA - timeB
    })

    // Calculate Distance
    let totalDistanceM = 0
    if (sortedFeatures.length > 1) {
      for (let i = 0; i < sortedFeatures.length - 1; i++) {
        const p1 = getCentroid(sortedFeatures[i].geometry)
        const p2 = getCentroid(sortedFeatures[i + 1].geometry)

        let c1 = p1
        let c2 = p2
        // Simple check for EPSG:3857 vs EPSG:4326
        if (Math.abs(c1[0]) > 180 || Math.abs(c1[1]) > 90) {
          c1 = toLonLat(p1)
          c2 = toLonLat(p2)
        }

        totalDistanceM += getDistance(c1, c2)
      }
    }

    const totalAreaSqm = sumBy(
      features,
      (f) => parseFloat(f.properties.sq_m) || 0,
    )

    // Weighted Average Yield
    let totalYieldGrams = 0
    let totalAreaSqM = 0

    features.forEach((f) => {
      const y = parseFloat(f.properties.yldg_m2)
      const a = parseFloat(f.properties.sq_m)
      if (!isNaN(y) && !isNaN(a) && a > 0) {
        totalYieldGrams += y * a
        totalAreaSqM += a
      }
    })

    const totalYieldInPref = unitConvert.convertYieldToPref(
      totalYieldGrams,
      units.totalyield_in,
      harvest.crop.buPerTonne,
    )

    const avgYieldGm2 = totalAreaSqM > 0 ? totalYieldGrams / totalAreaSqM : null

    const validMoisture = features
      .map((f) => parseFloat(f.properties.moisture))
      .filter((v) => !isNaN(v) && v > 0)

    const avgMoisture =
      validMoisture.length > 0 ? meanBy(validMoisture, (v) => v) : null

    // Speed
    const validSpeed = features
      .map((f) => parseFloat(f.properties.speedkmh))
      .filter((v) => !isNaN(v) && v >= 0)

    const avgSpeedKmh =
      validSpeed.length > 0 ? meanBy(validSpeed, (v) => v) : null

    // Convert yield
    let avgYieldDisplay = null
    if (avgYieldGm2 !== null) {
      const yieldInPref = unitConvert.convertYieldToPref(
        avgYieldGm2,
        units.yieldunit_in,
        harvest.crop.buPerTonne,
      )
      avgYieldDisplay = `${yieldInPref.toFixed(2)} ${t(units.yieldunit_in)}`
    }

    // Convert Speed
    let avgSpeedDisplay = null
    if (avgSpeedKmh !== null) {
      const speedInPref = unitConvert.convertSpeed(avgSpeedKmh, units.speed_in)
      avgSpeedDisplay = `${speedInPref.toFixed(1)} ${t(units.speed_in)}`
    }

    // Distance
    let totalDistanceinPref = totalDistanceM
    if (units.length_in === unitConvert.FEET) {
      totalDistanceinPref = unitConvert.convertToFeetInches(totalDistanceM).feet
    } else if (units.length_in === unitConvert.INCHES) {
      const { feet, inches } = unitConvert.convertToFeetInches(totalDistanceM)
      totalDistanceinPref = feet * 12 + inches
    }
    let distanceDisplay = `${totalDistanceinPref.toFixed(1)} ${t(units.length_in)}`

    // Area
    const areaVal = unitConvert.convertAreaToPref(totalAreaSqm, units.area_in)
    const areaDisplay = `${areaVal.toFixed(1)} ${t(units.area_in)}`

    const machine = startCase(
      features[0]?.properties?.combine ||
        features[0]?.properties?.Combine ||
        '-',
    )
    const operator = startCase(
      features[0]?.properties?.operator ||
        features[0]?.properties?.Operator ||
        '-',
    )

    return {
      count: features.length,
      avgMoisture: avgMoisture !== null ? `${avgMoisture.toFixed(1)} %` : '-',
      avgYield: avgYieldDisplay || '-',
      avgSpeed: avgSpeedDisplay || '-',
      totalYield:
        totalYieldInPref > 0
          ? `${totalYieldInPref.toFixed(2)} ${t(units.totalyield_in)}`
          : '-',
      crop: features[0]?.properties?.crop || '-',
      machine: machine,
      operator: operator,
      distance: distanceDisplay,
      area: areaDisplay,
    }
  }, [data, units, t, harvest])

  if (data === c.UNDEFINED) {
    return (
      <StyledContainer>
        <Busy />
      </StyledContainer>
    )
  }

  if (!stats) {
    return <Typography>{t('no_data')}</Typography>
  }

  return (
    <StyledContainer>
      <V>
        <StatRow>
          <Label variant="body2">{t('crop')}:</Label>
          <Value variant="body2">{stats.crop}</Value>
        </StatRow>
        <StatRow>
          <Label variant="body2">{t('operator')}:</Label>
          <Value variant="body2">{stats.operator}</Value>
        </StatRow>
        <StatRow>
          <Label variant="body2">{t('combine')}:</Label>
          <Value variant="body2">{stats.machine}</Value>
        </StatRow>
        <Divider sx={{ my: 1 }} />
        <StatRow>
          <Label variant="body2">{t('swath_distance')}:</Label>
          <Value variant="body2">{stats.distance}</Value>
        </StatRow>
        <StatRow>
          <Label variant="body2">{t('swath_area')}:</Label>
          <Value variant="body2">{stats.area}</Value>
        </StatRow>
        <Divider sx={{ my: 1 }} />
        <StatRow>
          <Label variant="body2">{t('total_yield')}:</Label>
          <Value variant="body2">{stats.totalYield}</Value>
        </StatRow>
        <StatRow>
          <Label variant="body2">{t('avg_yield')}:</Label>
          <Value variant="body2">{stats.avgYield}</Value>
        </StatRow>
        <StatRow>
          <Label variant="body2">{t('avg_moisture')}:</Label>
          <Value variant="body2">{stats.avgMoisture}</Value>
        </StatRow>
        <StatRow>
          <Label variant="body2">{t('avg_speed')}:</Label>
          <Value variant="body2">{stats.avgSpeed}</Value>
        </StatRow>
        <StatRow>
          <Label variant="body2">{t('point_count')}:</Label>
          <Value variant="body2">{stats.count}</Value>
        </StatRow>
      </V>
    </StyledContainer>
  )
}
